import { useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { useLanguages } from '@/hooks/use-languages';
import { getDefaultLanguages } from '@/lib/language.utils';
import { safePlay } from '@/lib/audio-playback';
import { transcriptionApi } from '@/api/transcription.api';
import { sourceParam, transcriptionSourceOf, type TranscriptionSource } from '@/lib/transcription-source';
import { useSessionDeepLink } from '@/hooks/use-session-deeplink';
import { useValidationGate, VALIDATION_GATE_QUERY_KEY } from '@/features/validation-gate';
import { useAudioWaveform } from '@/features/contribution-kit';
import type { TranscriptionOutcome, ValidationAudioItem } from './types';
import {
  VALIDATE_TRANSCRIPTION_PAGE_SIZE,
  decisionErrorDescription,
  finalizeErrorDescription,
  finalizePayload,
  isBatchComplete,
  markHandled,
  nextIndex,
  normalizeTranscriptionBatch,
  previousIndex,
  rejectPayload,
  transcriptionProgress,
  validatePayload,
} from './validate-transcription.session';

export function useValidateTranscriptionSession() {
  const { t, user, language, theme } = useApp();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeLanguages } = useLanguages();
  const queryClient = useQueryClient();
  const validationGate = useValidationGate();
  const s = useCallback((key: string) => String((t as Record<string, unknown>)[key] ?? ''), [t]);

  const languages = activeLanguages.length > 0 ? activeLanguages : getDefaultLanguages();

  const currentLang = useMemo(() => {
    const param = window.location.pathname.split('/')[1];
    return param || language || 'fr';
  }, [language]);

  const [selectedLanguage, setSelectedLanguage] = useState('');
  // In the URL, so a link can open the initial-data answers queue directly.
  const source = transcriptionSourceOf(searchParams.get('source'));
  const setSource = useCallback(
    (next: TranscriptionSource) =>
      setSearchParams((current) => {
        const params = new URLSearchParams(current);
        if (next === 'all') params.delete('source');
        else params.set('source', next);
        return params;
      }, { replace: true }),
    [setSearchParams],
  );
  const [sessionStarted, setSessionStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [items, setItems] = useState<ValidationAudioItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [text, setText] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [detailedRejectionReason, setDetailedRejectionReason] = useState('');

  const audioRef = useRef<HTMLAudioElement>(null);
  const current = items[currentIndex] ?? null;
  const { canvasRef } = useAudioWaveform({ audioRef, audioUrl: current?.audioUrl, theme });

  const { validatedCount, progressPercent } = transcriptionProgress(items);
  const sessionComplete = isBatchComplete(items);

  const startSession = useCallback(async () => {
    if (!selectedLanguage || !user?.id) return;
    setIsLoading(true);

    try {
      const res = await transcriptionApi.getAudiosWithRandomTranscription({
        lang: selectedLanguage,
        page: 1,
        limit: VALIDATE_TRANSCRIPTION_PAGE_SIZE,
        source: sourceParam(source),
      });

      const normalized = normalizeTranscriptionBatch(res);

      if (normalized.length > 0) {
        setItems(normalized);
        setCurrentIndex(0);
        setText(normalized[0].text || '');
        setSessionStarted(true);
      } else {
        toast({ title: s('noAudioAvailable'), description: s('retryLater') });
      }
    } catch (error) {
      console.error('Session start error', error);
      toast({ title: s('error'), description: s('audioLoadError'), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedLanguage, source, user?.id, s, toast]);

  const { startLinkedSession } = useSessionDeepLink({
    searchParams,
    setSearchParams,
    sessionValue: 'validate-transcription',
    selectedLanguage,
    setSelectedLanguage,
    sessionStarted,
    isStartingSession: isLoading,
    startSession,
  });

  const playAudio = useCallback(() => {
    const element = audioRef.current;
    if (!element) return;
    if (isPlaying) {
      element.pause();
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    safePlay(element, { onError: () => setIsPlaying(false) });
  }, [isPlaying]);

  const onAudioEnded = useCallback(() => setIsPlaying(false), []);

  const goNext = useCallback(() => {
    const next = nextIndex(currentIndex, items.length);
    if (next.ended) {
      toast({ title: s('sessionEnded') });
      return;
    }
    setCurrentIndex(next.index);
    setText(items[next.index].text || '');
  }, [currentIndex, items, toast, s]);

  const previous = useCallback(() => {
    const prevIndex = previousIndex(currentIndex);
    if (prevIndex === currentIndex) return;
    setCurrentIndex(prevIndex);
    setText(items[prevIndex].text || '');
  }, [currentIndex, items]);

  const markDone = useCallback((index: number, outcome: TranscriptionOutcome) => {
    setItems((prev) => markHandled(prev, index, outcome));
  }, []);

  const validateText = useCallback(async () => {
    if (!current) {
      toast({ title: s('noItemSelected'), variant: 'destructive' });
      return;
    }
    // Empêcher double-clic
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await transcriptionApi.validate(validatePayload(current, text));
      void queryClient.invalidateQueries({ queryKey: VALIDATION_GATE_QUERY_KEY });
      markDone(currentIndex, 'validated');
      goNext();
      toast({ title: s('validatedToast') });
    } catch (err: unknown) {
      toast({ title: s('error'), description: decisionErrorDescription(err, s('validationFailed')), variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }, [current, currentIndex, text, markDone, goNext, toast, s, isSubmitting, queryClient]);

  const rejectText = useCallback(async () => {
    if (!current) {
      toast({ title: s('noItemSelected'), variant: 'destructive' });
      return;
    }
    // Empêcher double-clic
    if (isSubmitting) return;

    if (!rejectionReason) {
      toast({ title: s('rejectionReasonRequired'), variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      await transcriptionApi.validate(rejectPayload(current, text, rejectionReason, detailedRejectionReason));
      void queryClient.invalidateQueries({ queryKey: VALIDATION_GATE_QUERY_KEY });
      markDone(currentIndex, 'rejected');
      setRejectionReason('');
      setDetailedRejectionReason('');
      goNext();
      toast({ title: s('rejectedToast') });
    } catch (err: unknown) {
      toast({ title: s('error'), description: decisionErrorDescription(err, s('rejectFailed')), variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }, [current, currentIndex, text, rejectionReason, detailedRejectionReason, markDone, goNext, toast, s, isSubmitting, queryClient]);

  const finalizeText = useCallback(async () => {
    if (!current) return;
    // Empêcher double-clic
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await transcriptionApi.finalize(finalizePayload(current, text));
      void queryClient.invalidateQueries({ queryKey: VALIDATION_GATE_QUERY_KEY });
      markDone(currentIndex, 'finalized');
      goNext();
      toast({ title: s('finalizedToast') });
    } catch (err: unknown) {
      toast({ title: s('error'), description: finalizeErrorDescription(err, s('finalizeError')), variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }, [current, currentIndex, text, markDone, goNext, toast, s, isSubmitting, queryClient]);

  return {
    // State
    selectedLanguage, setSelectedLanguage,
    source, setSource,
    sessionStarted, setSessionStarted,
    isLoading,
    isSubmitting,
    items, currentIndex,
    isPlaying,
    text, setText,
    rejectionReason, setRejectionReason,
    detailedRejectionReason, setDetailedRejectionReason,
    current,
    audioRef,
    canvasRef,
    currentLang,
    languages,
    validationGate,
    user,
    // Derived
    validatedCount,
    progressPercent,
    sessionComplete,
    itemsLength: items.length,
    // Actions
    startLinkedSession,
    /** A fresh batch with the same language and source (end screen). */
    restartSession: startSession,
    playAudio,
    onAudioEnded,
    goNext,
    previous,
    validateText,
    rejectText,
    finalizeText,
  };
}
